import { defineComponent, computed, ref, onMounted, onBeforeUnmount, watch, createElementBlock, openBlock, normalizeClass, createCommentVNode, normalizeStyle, createElementVNode, Fragment, toDisplayString } from "vue";
import { toneForHex, POS_TONES } from "app-theme";
import { registerSalesTile, unregisterSalesTile, invalidateSalesTile } from "./index108.js";
const _hoisted_1 = ["data-tone", "disabled"];
const _hoisted_2 = {
  key: 0,
  class: "v2-ghost"
};
const _hoisted_3 = { class: "v2-name" };
const _hoisted_4 = ["disabled"];
const _hoisted_5 = { class: "v1-name" };
const _hoisted_6 = {
  key: 2,
  class: "badge"
};
const _sfc_main = /* @__PURE__ */ defineComponent({
  __name: "SalesButtonFace",
  props: {
    config: {
      type: Object,
      default: () => {
      }
    },
    disabled: {
      type: Boolean,
      default: false
    },
    badge: {
      type: Number,
      default: 0
    },
    selected: {
      type: Boolean,
      default: false
    },
    // Opt-in (POS retail only): scale the label with the button's own box via a
    // size container. Hospitality/admin leave this off (fixed label size).
    scaleText: {
      type: Boolean,
      default: false
    },
    // The inherit FALLBACK for articles whose own color is NEUTRAL (design §5
    // "boja je navigacija" + owner 2026-07-17: an article's own tone wins) —
    // the grid host passes the active category button's canonical tone hex.
    categoryTone: {
      type: String,
      default: null
    }
  },
  setup(__props) {
    const props = __props;
    const isV2 = computed(() => {
      var _a;
      return (((_a = props.config) == null ? void 0 : _a.version) ?? 1) >= 2;
    });
    const isCategoryTile = computed(() => !!props.config && !props.config.product_id && (!!props.config.category_id || !!props.config.group_id));
    const tone = computed(() => {
      if (!props.config) return void 0;
      if (isCategoryTile.value) return toneForHex(props.config.background_color ?? "");
      return toneForHex(props.config.background_color ?? "") || (props.categoryTone ? toneForHex(props.categoryTone) : void 0);
    });
    const isSolidTile = computed(() => isCategoryTile.value || !!tone.value);
    const isImageTile = computed(() => {
      var _a;
      return !!((_a = props.config) == null ? void 0 : _a.image_url);
    });
    const toneVar = computed(() => tone.value ? `var(--pos-tone-${tone.value}, ${POS_TONES[tone.value]})` : null);
    const v2Style = computed(() => {
      if (isImageTile.value) {
        return {
          backgroundColor: toneVar.value ?? "var(--ui-bg-accented)",
          backgroundImage: `url(${props.config.image_url})`,
          backgroundSize: "contain",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat"
        };
      }
      if (isSolidTile.value) {
        return toneVar.value ? { background: toneVar.value } : { background: "var(--ui-bg-accented)", color: "var(--ui-text)" };
      }
      return {
        background: "var(--ui-bg)",
        borderColor: "var(--ui-border)"
      };
    });
    const v2ChipStyle = computed(() => toneVar.value ? {
      background: "rgba(255, 255, 255, 0.22)",
      // white-on-solid (design §2)
      color: "#ffffff"
    } : {
      background: "var(--ui-bg-accented)",
      color: "var(--ui-text-muted)"
    });
    const monogram = computed(() => {
      var _a;
      const words = (((_a = props.config) == null ? void 0 : _a.text) || "").trim().toUpperCase().split(/\s+/).filter(Boolean);
      if (!words.length) return "";
      const first = Array.from(words[0]);
      if (isCategoryTile.value) return first[0];
      return words.length > 1 ? first[0] + Array.from(words[1])[0] : first.slice(0, 2).join("");
    });
    const rootRef = ref(null);
    onMounted(() => {
      if (rootRef.value) registerSalesTile(rootRef.value);
    });
    onBeforeUnmount(() => {
      if (rootRef.value) unregisterSalesTile(rootRef.value);
    });
    watch(
      () => {
        var _a;
        return [(_a = props.config) == null ? void 0 : _a.text, isV2.value, isImageTile.value, isSolidTile.value, isCategoryTile.value];
      },
      () => {
        if (rootRef.value) invalidateSalesTile(rootRef.value);
      },
      { flush: "post" }
    );
    const hasPhoto = computed(() => {
      var _a;
      return !!((_a = props.config) == null ? void 0 : _a.image_url);
    });
    const PHOTO_SHADE = {
      top: "linear-gradient(to bottom, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0) 44px)",
      bottom: "linear-gradient(to top, rgba(0, 0, 0, 0.6), rgba(0, 0, 0, 0) 44px)",
      center: "rgba(0, 0, 0, 0.3)"
    };
    const contentStyle = computed(() => {
      var _a;
      if (!props.config) {
        return {};
      }
      return {
        "alignItems": props.config.text_valign === "top" ? "flex-start" : props.config.text_valign === "bottom" ? "flex-end" : "center",
        "justifyContent": props.config.text_halign === "left" ? "flex-start" : props.config.text_halign === "right" ? "flex-end" : "center",
        "backgroundImage": ((_a = props.config) == null ? void 0 : _a.image_url) ? `url(${props.config.image_url})` : void 0,
        "backgroundPosition": props.config ? `${props.config.image_halign} ${props.config.image_valign}` : "center",
        "backgroundSize": props.config ? props.config.image_stretch ? "cover" : props.config.image_fit ? "contain" : "50%" : void 0
      };
    });
    function luminance(hex) {
      const m = (hex ?? "").replace("#", "").match(/^([0-9a-f]{3}|[0-9a-f]{6})$/i);
      if (!m) return 0;
      let h = m[1];
      if (h.length === 3) h = h.split("").map((c) => c + c).join("");
      const r = parseInt(h.slice(0, 2), 16);
      const g = parseInt(h.slice(2, 4), 16);
      const b = parseInt(h.slice(4, 6), 16);
      return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    }
    const isLightBackground = computed(() => {
      var _a;
      return luminance((_a = props.config) == null ? void 0 : _a.background_color) > 0.85;
    });
    const style = computed(() => {
      return {
        background: props.config.background_color ?? void 0,
        color: isLightBackground.value && !hasPhoto.value ? "#606266" : "#ffffff",
        textShadow: hasPhoto.value ? "var(--sbf-image-name-shadow)" : void 0,
        "--v1-photo-shade": hasPhoto.value ? PHOTO_SHADE[props.config.text_valign === "top" || props.config.text_valign === "bottom" ? props.config.text_valign : "center"] : void 0
      };
    });
    return (_ctx, _cache) => {
      return openBlock(), createElementBlock("div", {
        ref_key: "rootRef",
        ref: rootRef,
        class: normalizeClass(["sales-button-face", { "scale-text": __props.scaleText }])
      }, [
        isV2.value ? (openBlock(), createElementBlock("button", {
          key: 0,
          type: "button",
          class: normalizeClass(["btn v2", [
            isImageTile.value ? "v2-image" : isSolidTile.value ? "v2-solid" : "v2-tint",
            { selected: props.selected, "v2-article": !isCategoryTile.value && !isImageTile.value }
          ]]),
          style: normalizeStyle(v2Style.value),
          "data-tone": tone.value,
          disabled: __props.disabled
        }, [
          !isImageTile.value && monogram.value ? (openBlock(), createElementBlock(Fragment, { key: 0 }, [
            isCategoryTile.value ? (openBlock(), createElementBlock("span", _hoisted_2, toDisplayString(monogram.value), 1)) : (openBlock(), createElementBlock("span", {
              key: 1,
              class: "v2-chip",
              style: normalizeStyle(v2ChipStyle.value)
            }, toDisplayString(monogram.value), 5))
          ], 64)) : createCommentVNode("", true),
          createElementVNode("span", _hoisted_3, toDisplayString(__props.config.text), 1)
        ], 14, _hoisted_1)) : (openBlock(), createElementBlock("button", {
          key: 1,
          type: "button",
          class: normalizeClass(["btn", {
            "selected": props.selected,
            "bordered": isLightBackground.value,
            "photo-shade": hasPhoto.value
          }]),
          style: normalizeStyle(style.value),
          disabled: __props.disabled
        }, [
          createElementVNode("div", {
            class: "content",
            style: normalizeStyle(contentStyle.value)
          }, [
            createElementVNode("span", _hoisted_5, toDisplayString(__props.config.text), 1)
          ], 4)
        ], 14, _hoisted_4)),
        __props.badge ? (openBlock(), createElementBlock("span", _hoisted_6, toDisplayString(__props.badge), 1)) : createCommentVNode("", true)
      ], 2);
    };
  }
});
export {
  _sfc_main as default
};
//# sourceMappingURL=index70.js.map
